/**
 * Hole Visualisation Page
 *
 * Visualises a golf hole from above and simulates shot outcomes
 * based on configurable player stats. Shows heatmaps of landing
 * zones and score distributions for different strategies.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { PageTitle, H2Heading } from '../../../components/Headers';
import { PageBody } from '../../../components/PageBody';
import { ArticleParagraph } from '../../../components/Paragraphs';
import { HoleHexGrid } from './components/HoleHexGrid';
import {
  createPlayerStats,
  simulateMultipleHoles,
  DEFAULT_BRACKETS,
  SCORE_ORDER,
  SCORE_COLOURS,
  STRATEGY_ARCHETYPES,
  PUTTING_REFERENCE_DATA,
  PUTTING_SOURCES,
} from './utility/holeSimulation';
import { HOLE_CATALOGUE, createHoleLayoutFromHex } from './utility/holeLoader';

const BRACKET_LABELS = {
  driver: 'Driver',
  longIron: 'Long Iron',
  midIron: 'Mid Iron',
  shortIron: 'Short Iron',
  wedge: 'Wedge',
  pitch: 'Pitch',
};

const BRACKET_RANGES = {
  power: { min: 40, max: 280, step: 5 },
  distanceStdDev: { min: 2, max: 30, step: 1 },
  lateralStdDev: { min: 1, max: 30, step: 1 },
};

function medianValue(arr) {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

export const HoleVisualisation = () => {
  // Hole selection
  const [selectedHoleId, setSelectedHoleId] = useState(HOLE_CATALOGUE[0].id);

  // Player stat overrides
  const [brackets, setBrackets] = useState(() => {
    const init = {};
    for (const [key, vals] of Object.entries(DEFAULT_BRACKETS)) {
      init[key] = { ...vals };
    }
    return init;
  });
  const [strategy, setStrategy] = useState('aggressive');
  const [simCount, setSimCount] = useState(500);
  const [selectedShot, setSelectedShot] = useState('all');
  const [selectedScore, setSelectedScore] = useState(null);
  const [selectedHazards, setSelectedHazards] = useState(new Set());
  const [replayResult, setReplayResult] = useState(null);
  const [simResult, setSimResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showWaypoints, setShowWaypoints] = useState(false);

  // Build hole layout from selected hole data
  const holeData = useMemo(() => HOLE_CATALOGUE.find(h => h.id === selectedHoleId) || HOLE_CATALOGUE[0], [selectedHoleId]);
  const hole = useMemo(() => createHoleLayoutFromHex(holeData), [holeData]);

  // Clear results when hole changes
  const handleHoleChange = useCallback((id) => {
    setSelectedHoleId(id);
    setSimResult(null);
    setReplayResult(null);
    setSelectedShot('all');
    setSelectedScore(null);
    setSelectedHazards(new Set());
  }, []);

  // Build player from current bracket state
  const player = useMemo(() => createPlayerStats(brackets), [brackets]);

  // Run simulation
  const runSimulation = useCallback(() => {
    setIsRunning(true);
    setReplayResult(null);
    // Defer to next frame so spinner shows
    requestAnimationFrame(() => {
      const result = simulateMultipleHoles(hole, player, strategy, simCount);
      setSimResult(result);
      setSelectedShot('all');
      setSelectedScore(null);
      setSelectedHazards(new Set());
      setIsRunning(false);
    });
  }, [hole, player, strategy, simCount]);

  // Compute landings for selected shot number, filtered by score and hazards
  const displayLandings = useMemo(() => {
    if (!simResult) return [];

    let landings;
    if (selectedShot === 'all') {
      landings = Object.values(simResult.landingsByShot).flat();
    } else {
      landings = simResult.landingsByShot[parseInt(selectedShot)] || [];
    }

    // Build set of allowed result indices from all active filters
    let allowedIndices = null;

    // Score filter
    if (selectedScore && simResult.resultsByScore[selectedScore]) {
      allowedIndices = new Set(simResult.resultsByScore[selectedScore]);
    }

    // Hazard filter (intersect with score filter if both active)
    if (selectedHazards.size > 0) {
      const hazardIndices = new Set();
      for (const hazard of selectedHazards) {
        for (const idx of (simResult.hazardHits[hazard] || [])) {
          if (!allowedIndices || allowedIndices.has(idx)) {
            hazardIndices.add(idx);
          }
        }
      }
      allowedIndices = hazardIndices;
    }

    if (allowedIndices) {
      landings = landings.filter(l => allowedIndices.has(l.resultIndex));
    }

    return landings;
  }, [simResult, selectedShot, selectedScore, selectedHazards]);

  // Terrain breakdown for the currently displayed landings
  const terrainBreakdown = useMemo(() => {
    if (!displayLandings.length) return null;
    const total = displayLandings.length;

    // Count "landed on" terrains — use penaltyReason to show what the ball
    // actually hit (water/oob) rather than the drop zone terrain
    const landed = {};
    const from = {};
    for (const l of displayLandings) {
      const effectiveTerrain = l.penaltyReason || l.terrain;
      landed[effectiveTerrain] = (landed[effectiveTerrain] || 0) + 1;
      if (l.fromTerrain) {
        from[l.fromTerrain] = (from[l.fromTerrain] || 0) + 1;
      }
    }

    // Sort by count descending, format as { terrain, count, pct }
    const format = (counts) =>
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([terrain, count]) => ({
          terrain,
          count,
          pct: ((count / total) * 100).toFixed(0),
        }));

    return { total, landed: format(landed), from: format(from) };
  }, [displayLandings]);

  // Available shot numbers
  const shotNumbers = useMemo(() => {
    if (!simResult) return [];
    return Object.keys(simResult.landingsByShot).map(Number).sort((a, b) => a - b);
  }, [simResult]);

  // Representative aim line for the current filter combination.
  // When any filter is active, derive from the filtered landing set (median from/target).
  // Falls back to the first simulation's shot targets when no filters are active.
  const shotAimLine = useMemo(() => {
    if (!simResult || !displayLandings.length) return null;
    const anyFilter = selectedShot !== 'all' || selectedScore !== null || selectedHazards.size > 0;
    if (!anyFilter) return null;
    const withTargets = displayLandings.filter(l => l.fromX != null && l.targetX != null);
    if (!withTargets.length) return null;
    return {
      from: {
        x: medianValue(withTargets.map(l => l.fromX)),
        y: medianValue(withTargets.map(l => l.fromY)),
      },
      to: {
        x: medianValue(withTargets.map(l => l.targetX)),
        y: medianValue(withTargets.map(l => l.targetY)),
      },
    };
  }, [simResult, selectedShot, selectedScore, selectedHazards, displayLandings]);

  // Starting positions for the selected shot — shown as a blue overlay heatmap.
  const startPositions = useMemo(() => {
    if (!simResult || selectedShot === 'all' || !displayLandings.length) return [];
    return displayLandings
      .filter(l => l.fromX != null)
      .map(l => ({ x: l.fromX, y: l.fromY }));
  }, [simResult, selectedShot, displayLandings]);

  // Club selection breakdown for the currently displayed landings.
  const clubBreakdown = useMemo(() => {
    if (!displayLandings.length) return null;
    const counts = {};
    for (const l of displayLandings) {
      if (l.club) counts[l.club] = (counts[l.club] || 0) + 1;
    }
    const total = displayLandings.length;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([club, count]) => ({ club, count, pct: ((count / total) * 100).toFixed(0) }));
  }, [displayLandings]);

  // Update a single bracket stat
  const updateBracket = useCallback((bracket, stat, value) => {
    setBrackets(prev => ({
      ...prev,
      [bracket]: { ...prev[bracket], [stat]: parseFloat(value) },
    }));
  }, []);

  // Pick a random hole for replay
  const pickRandomHole = useCallback(() => {
    if (!simResult) return;
    let pool = simResult.results;
    if (selectedScore && simResult.resultsByScore[selectedScore]) {
      pool = simResult.resultsByScore[selectedScore].map(i => simResult.results[i]);
    }
    if (pool.length === 0) return;
    const idx = Math.floor(Math.random() * pool.length);
    setReplayResult(pool[idx]);
  }, [simResult, selectedScore]);

  const clearReplay = useCallback(() => {
    setReplayResult(null);
  }, []);


  // Score filter toggle
  const toggleScoreFilter = useCallback((label) => {
    setSelectedScore(prev => prev === label ? null : label);
  }, []);

  // Hazard filter toggle
  const toggleHazardFilter = useCallback((hazard) => {
    setSelectedHazards(prev => {
      const next = new Set(prev);
      if (next.has(hazard)) next.delete(hazard);
      else next.add(hazard);
      return next;
    });
  }, []);

  return (
    <PageBody>
        <Helmet>
          <title>Shot-Level Simulation | VizBadger</title>
          <meta name="description" content="Simulate golf hole shot patterns using Monte Carlo methods. See heatmaps of landing zones and compare aggressive vs conservative strategies." />
        </Helmet>

        <PageTitle title="Shot-Level Simulation" />

        {/* Desktop description */}
        <div className="hidden md:block mb-4">
          <ArticleParagraph lines={[
            'This page simulates shot outcomes on a golf hole to build intuition about shot strategy. Each simulation runs hundreds of rounds, showing where shots land as heatmaps overlaid on the hole layout.',
            'Adjust player stats below to model different skill levels, then compare aggressive and conservative strategies to see how they affect scoring.',
          ]} />
        </div>
        
        {/* Mobile description - shorter */}
        <div className="md:hidden mb-4 text-sm text-gray-600">
          <p>Simulate shot outcomes on a golf hole. Run simulations to see heatmaps of landing zones.</p>
        </div>

        {/* Hole visualisation */}
        <div className="mb-2 md:mb-3">
          <HoleHexGrid
            hole={hole}
            landings={replayResult ? [] : displayLandings}
            showHeatmap={simResult !== null && !replayResult}
            aimPoints={
              replayResult ? [] :
              shotAimLine ? [shotAimLine] :
              simResult ? simResult.aimPoints.map(pt => ({ from: null, to: pt })) :
              []
            }
            startPositions={replayResult ? [] : startPositions}
            replayShots={replayResult || null}
            showLegend={true}
            showWaypoints={showWaypoints}
          />
        </div>

        {/* Active filters summary row */}
        {simResult && (
          <div className="flex flex-wrap items-center gap-2 mb-3 md:mb-4 min-h-[24px]">
            {(selectedScore || selectedHazards.size > 0 || selectedShot !== 'all') ? (
              <>
                <span className="text-xs text-gray-500">Active filters:</span>
                {selectedScore && (
                  <button
                    onClick={() => setSelectedScore(null)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300 hover:bg-green-200 min-h-[28px]"
                  >
                    {selectedScore} <span aria-hidden="true">×</span>
                  </button>
                )}
                {selectedShot !== 'all' && (
                  <button
                    onClick={() => setSelectedShot('all')}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 min-h-[28px]"
                  >
                    Shot {selectedShot} <span aria-hidden="true">×</span>
                  </button>
                )}
                {[...selectedHazards].map(h => (
                  <button
                    key={h}
                    onClick={() => toggleHazardFilter(h)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300 hover:bg-orange-200 min-h-[28px]"
                  >
                    {h.charAt(0).toUpperCase() + h.slice(1)} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </>
            ) : (
              <span className="text-xs text-gray-400">No filters active</span>
            )}
          </div>
        )}

        {/* Controls row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        {/* Strategy selector */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4">
          {/* Hole selector */}
          <div className="mb-3 md:mb-4">
            <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2">Hole</h3>
            <select
              value={selectedHoleId}
              onChange={e => handleHoleChange(e.target.value)}
              className="w-full text-xs md:text-sm border border-gray-300 rounded px-2 py-1.5"
            >
              {HOLE_CATALOGUE.map(h => (
                <option key={h.id} value={h.id}>
                  {h.name} (Par {h.par})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{holeData.description}</p>
            <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showWaypoints}
                onChange={e => setShowWaypoints(e.target.checked)}
                className="accent-yellow-500"
              />
              <span className="text-xs text-gray-600">Show waypoints &amp; drop zones</span>
            </label>
          </div>

          <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">Strategy</h3>
          <div className="space-y-2">
            {Object.entries(STRATEGY_ARCHETYPES).map(([key, arch]) => (
              <label key={key} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="strategy"
                  value={key}
                  checked={strategy === key}
                  onChange={() => setStrategy(key)}
                  className="mt-0.5 accent-green-600"
                />
                <div>
                  <span className="text-sm font-medium text-gray-800">{arch.label}</span>
                  <p className="text-xs text-gray-500">{arch.description}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-3 md:mt-4">
            <label className="text-xs text-gray-500">Simulations</label>
            <select
              value={simCount}
              onChange={e => setSimCount(parseInt(e.target.value))}
              className="ml-2 text-xs md:text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1,000</option>
              <option value={2000}>2,000</option>
            </select>
          </div>
          <div className="mt-3 md:mt-4 flex gap-2">
            <button
              onClick={runSimulation}
              disabled={isRunning}
              className="flex-1 py-2 px-4 rounded-lg text-xs md:text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 transition-colors"
            >
              {isRunning ? 'Simulating...' : 'Simulate'}
            </button>
            {simResult && (
              <button
                onClick={() => { setSimResult(null); setReplayResult(null); setSelectedScore(null); setSelectedHazards(new Set()); }}
                className="py-2 px-3 rounded-lg text-xs md:text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Score distribution (clickable filter) */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4">
          <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">Score Distribution</h3>
          {simResult ? (
            <>
              <div className="space-y-1.5">
                {SCORE_ORDER.map(label => {
                  const count = (simResult.resultsByScore[label] || []).length;
                  const pct = parseFloat(simResult.scoreDistribution[label] || 0);
                  const isEmpty = count === 0;
                  const isActive = selectedScore === label;
                  const isDimmed = selectedScore && !isActive;
                  return (
                    <button
                      key={label}
                      onClick={() => !isEmpty && toggleScoreFilter(label)}
                      disabled={isEmpty}
                      className={`flex items-center gap-2 w-full text-left rounded px-1 py-0.5 transition-all min-h-[32px] ${
                        isEmpty ? 'opacity-30 cursor-default' :
                        isDimmed ? 'opacity-25' :
                        isActive ? 'bg-green-50' :
                        'hover:bg-gray-50'
                      }`}
                    >
                      <span className={`text-xs w-14 text-right shrink-0 ${isActive ? 'font-bold text-green-800' : 'text-gray-600'}`}>
                        {label}
                      </span>
                      <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                        <div
                          className={`h-full rounded transition-all ${isActive ? 'ring-2 ring-offset-2 ring-green-600 shadow' : ''}`}
                          style={{
                            width: `${Math.max(pct, 0.5)}%`,
                            backgroundColor: isEmpty ? '#D1D5DB' : SCORE_COLOURS[label],
                          }}
                        />
                      </div>
                      <span className={`text-xs w-20 text-right shrink-0 whitespace-nowrap ${isActive ? 'font-semibold text-green-800' : 'text-gray-700'}`}>
                        {count} ({pct}%)
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Average score: <span className="font-medium">{simResult.avgScore.toFixed(2)}</span>
                {' '}({(simResult.avgScore - hole.par) >= 0 ? '+' : ''}{(simResult.avgScore - hole.par).toFixed(2)} to par)
              </p>
              {selectedScore && (
                <p className="text-xs text-gray-400 mt-1">Click again to clear</p>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400 italic">Run a simulation to see results</p>
          )}
        </div>

        {/* Shot selector + Hole replay */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4">
          <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">Heatmap View</h3>
          {simResult ? (
            <>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedShot('all')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    selectedShot === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {shotNumbers.map(n => {
                  const shotLandings = simResult.landingsByShot[n]?.length ?? 0;
                  const isActive = selectedShot === String(n);
                  return (
                    <button
                      key={n}
                      onClick={() => setSelectedShot(String(n))}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors min-h-[32px] ${
                        isActive ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Shot {n} <span className={`ml-1 ${isActive ? 'text-green-100' : 'text-gray-400'}`}>({shotLandings})</span>
                    </button>
                  );
                })}
              </div>

              {/* Hazard filter chips */}
              {Object.values(simResult.hazardHits).some(arr => arr.length > 0) && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-xs text-gray-500 mr-0.5">Hazards:</span>
                  {[
                    { key: 'bunker', label: 'Bunker', colour: '#D4B896', textColour: '#333' },
                    { key: 'water', label: 'Water', colour: '#42A5F5', textColour: '#fff' },
                    { key: 'oob', label: 'OOB', colour: '#2E7D32', textColour: '#fff' },
                  ].map(({ key, label, colour, textColour }) => {
                    const count = simResult.hazardHits[key]?.length || 0;
                    if (count === 0) return null;
                    const isActive = selectedHazards.has(key);
                    return (
                      <button
                        key={key}
                        onClick={() => toggleHazardFilter(key)}
                        className={`px-2 py-0.5 rounded text-xs font-medium transition-all min-h-[28px] ${
                          isActive ? 'ring-2 ring-offset-1 ring-green-500 opacity-100' : 'opacity-70 hover:opacity-100'
                        }`}
                        style={{ backgroundColor: colour, color: textColour }}
                      >
                        {label} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedShot !== 'all' && (
                <p className="text-xs text-gray-500 mt-2">
                  {displayLandings.length} landing{displayLandings.length !== 1 ? 's' : ''} shown for shot {selectedShot}
                  {startPositions.length > 0 && <span className="text-blue-500 ml-1">· blue = starting positions</span>}
                </p>
              )}

              {/* Terrain breakdown */}
              {terrainBreakdown && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1.5">
                    <span className="font-medium text-gray-700">{terrainBreakdown.total.toLocaleString()}</span> landings shown
                  </p>
                  {terrainBreakdown.from.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-400">From:</span>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {terrainBreakdown.from.map(({ terrain, count, pct }) => (
                          <span key={terrain} className="text-xs text-gray-600 capitalize">
                            {terrain} <span className="text-gray-400">{count.toLocaleString()} ({pct}%)</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-gray-400">Landed:</span>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {terrainBreakdown.landed.map(({ terrain, count, pct }) => (
                        <span key={terrain} className="text-xs text-gray-600 capitalize">
                          {terrain} <span className="text-gray-400">{count.toLocaleString()} ({pct}%)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Club selection breakdown */}
              {clubBreakdown && clubBreakdown.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Club selection:</span>
                  <div className="flex flex-col gap-1 mt-1.5">
                    {clubBreakdown.map(({ club, count, pct }) => (
                      <div key={club} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 capitalize w-20 shrink-0">{club}</span>
                        <div className="flex-1 h-3 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full rounded bg-badger-purple opacity-70"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-16 text-right shrink-0">{count.toLocaleString()} ({pct}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hole replay */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Hole Replay</h4>
                <div className="flex gap-2">
                  <button
                    onClick={pickRandomHole}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    Pick a random hole
                  </button>
                  {replayResult && (
                    <button
                      onClick={clearReplay}
                      className="px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      Clear replay
                    </button>
                  )}
                </div>
                {replayResult && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Score: {replayResult.score} ({replayResult.toPar >= 0 ? '+' : ''}{replayResult.toPar} to par)
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="text-gray-500 border-b border-gray-200">
                            <th className="text-left py-1 pr-2">#</th>
                            <th className="text-right py-1 pr-2">Strokes</th>
                            <th className="text-left py-1 pr-2">Club</th>
                            <th className="text-left py-1 pr-2">From</th>
                            <th className="text-left py-1 pr-2">Landed</th>
                            <th className="text-right py-1 pr-2">Intended</th>
                            <th className="text-right py-1 pr-2">Actual</th>
                            <th className="text-right py-1 pr-2">Lateral</th>
                            <th className="text-left py-1">Context</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            let runningStrokes = 0;
                            return replayResult.shots.map((shot, i) => {
                              runningStrokes += 1 + (shot.penalty || 0);
                              const lateralLabel = shot.holed ? '\u2014'
                                : Math.abs(shot.lateralOffset) < 0.5 ? 'On line'
                                : `${Math.abs(shot.lateralOffset).toFixed(0)}m ${shot.lateralOffset > 0 ? 'R' : 'L'}`;
                              const hasPenalty = shot.penalty > 0;
                              const penaltyLabel = shot.penaltyReason === 'water' ? 'Water'
                                : shot.penaltyReason === 'oob' ? 'OOB' : null;
                              const landedLabel = shot.holed ? 'Holed'
                                : hasPenalty ? `${penaltyLabel} \u2192 drop (${shot.terrain})`
                                : shot.terrain;

                              return (
                                <tr key={i} className={`border-b border-gray-100${hasPenalty ? ' bg-red-50' : ''}`}>
                                  <td className="py-1 pr-2 font-medium">{shot.shotNumber}</td>
                                  <td className="py-1 pr-2 text-right font-medium">
                                    {runningStrokes}
                                    {hasPenalty && <span className="text-red-500 ml-1 text-[10px]">(+1 pen.)</span>}
                                  </td>
                                  <td className="py-1 pr-2 capitalize">{shot.club}</td>
                                  <td className="py-1 pr-2 capitalize">{shot.fromTerrain || '\u2014'}</td>
                                  <td className={`py-1 pr-2 capitalize${hasPenalty ? ' text-red-600 font-medium' : ''}`}>{landedLabel}</td>
                                  <td className="py-1 pr-2 text-right">{shot.holed ? '\u2014' : `${shot.intendedDistance}m`}</td>
                                  <td className="py-1 pr-2 text-right">{shot.holed ? '\u2014' : `${shot.actualDistance}m`}</td>
                                  <td className="py-1 pr-2 text-right">{lateralLabel}</td>
                                  <td className="py-1 capitalize text-gray-500">{shot.shotContext}</td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                    {replayResult.shots.some(s => s.penalty > 0) && (
                      <p className="text-xs text-red-500 mt-1">
                        {replayResult.shots.filter(s => s.penalty > 0).length} penalty stroke{replayResult.shots.filter(s => s.penalty > 0).length > 1 ? 's' : ''}: {
                          [...new Set(replayResult.shots.filter(s => s.penaltyReason).map(s => s.penaltyReason))]
                            .map(r => `${r === 'water' ? 'Water' : 'OOB'} (${replayResult.shots.filter(s => s.penaltyReason === r).length})`)
                            .join(', ')
                        }
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400 italic">Run a simulation to toggle shots</p>
          )}
        </div>
      </div>

      {/* Player stats */}
      <H2Heading>Player Stats</H2Heading>
      <ArticleParagraph lines={[
        'Configure power (max distance), distance variance, and lateral spread for each club bracket. Higher variance and spread model less consistent players.',
      ]} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {Object.entries(BRACKET_LABELS).map(([key, label]) => (
          <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">{label}</h4>
            {/* Power */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Power (max distance)</span>
                <span className="font-medium text-gray-700">{brackets[key].power}m</span>
              </div>
              <input
                type="range"
                min={BRACKET_RANGES.power.min}
                max={BRACKET_RANGES.power.max}
                step={BRACKET_RANGES.power.step}
                value={brackets[key].power}
                onChange={e => updateBracket(key, 'power', e.target.value)}
                className="w-full h-1.5 accent-green-600"
              />
            </div>
            {/* Distance std dev */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Distance variance</span>
                <span className="font-medium text-gray-700">&plusmn;{brackets[key].distanceStdDev}m</span>
              </div>
              <input
                type="range"
                min={BRACKET_RANGES.distanceStdDev.min}
                max={BRACKET_RANGES.distanceStdDev.max}
                step={BRACKET_RANGES.distanceStdDev.step}
                value={brackets[key].distanceStdDev}
                onChange={e => updateBracket(key, 'distanceStdDev', e.target.value)}
                className="w-full h-1.5 accent-blue-600"
              />
            </div>
            {/* Lateral spread */}
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Lateral spread</span>
                <span className="font-medium text-gray-700">&plusmn;{brackets[key].lateralStdDev}m</span>
              </div>
              <input
                type="range"
                min={BRACKET_RANGES.lateralStdDev.min}
                max={BRACKET_RANGES.lateralStdDev.max}
                step={BRACKET_RANGES.lateralStdDev.step}
                value={brackets[key].lateralStdDev}
                onChange={e => updateBracket(key, 'lateralStdDev', e.target.value)}
                className="w-full h-1.5 accent-orange-500"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Club selection */}
      <H2Heading>Club Selection</H2Heading>
      <ArticleParagraph lines={[
        'Club selection depends on shot context. On the tee and for layups, the longest allowed club is used (driver for Aggressive/Conservative, long iron for No Driver). For approach shots where the green is reachable, the shortest club with enough power is chosen for precision. From the rough or a bunker, the shortest club that can reach the fairway is selected.',
        'Terrain modifiers are per-club: a driver from a bunker loses 70% distance with 3x spread, while a wedge only loses 35% distance with 1.4x spread. When target distance falls between 90-100% of a smaller club\'s power, Aggressive strategies power down the bigger club (with a slight spread penalty) and Conservative strategies use the smaller club at full power.',
      ]} />

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-8 overflow-x-auto">
        <table className="text-sm w-full">
          <thead>
            <tr className="text-gray-500 border-b border-gray-200">
              <th className="text-left py-2 pr-4 font-medium">Club</th>
              <th className="text-right py-2 px-4 font-medium">Power</th>
              <th className="text-right py-2 px-4 font-medium">Used When Target Is</th>
              <th className="text-right py-2 px-4 font-medium">Distance Variance</th>
              <th className="text-right py-2 pl-4 font-medium">Lateral Spread</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const sorted = Object.entries(brackets)
                .sort((a, b) => a[1].power - b[1].power);
              return sorted.map(([key, stats], i) => {
                const prevPower = i > 0 ? sorted[i - 1][1].power : 0;
                const isLast = i === sorted.length - 1;
                const rangeLabel = isLast
                  ? `${prevPower + 1}m+ (or beyond all clubs)`
                  : `${prevPower + 1}–${stats.power}m`;
                return (
                  <tr key={key} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-800">{BRACKET_LABELS[key]}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{stats.power}m</td>
                    <td className="py-2 px-4 text-right text-gray-600">{rangeLabel}</td>
                    <td className="py-2 px-4 text-right text-gray-600">&plusmn;{stats.distanceStdDev}m</td>
                    <td className="py-2 pl-4 text-right text-gray-600">&plusmn;{stats.lateralStdDev}m</td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
        <p className="text-xs text-gray-500 mt-3">
          The table updates live as you adjust the player stats above. Terrain modifiers are per-club: longer clubs suffer more from rough and bunkers than short irons and wedges.
        </p>
      </div>

      {/* Methodology note */}
      <H2Heading>How It Works</H2Heading>
      <ArticleParagraph lines={[
        'Each shot is sampled from a 2D normal distribution. Distance follows N(intended, distanceStdDev) along the aim line, and lateral miss follows N(0, lateralStdDev) perpendicular to it. Terrain modifiers are per-club: rough penalises a driver far more than a wedge, and bunkers are devastating for long clubs but manageable for short ones.',
        'Three strategy archetypes control shot selection. Aggressive uses the driver off the tee and may cut corners over hazards. Conservative uses the driver but stays in the corridor, attacking the green at 90% club power. No Driver caps the tee shot at a long iron for extra safety. All strategies follow the waypoint corridor on dogleg holes and aim for the nearest fairway when recovering from trouble.',
        'Water and out-of-bounds landings add a penalty stroke and use a drop zone. On the green, putting uses distance-based make probabilities calibrated to amateur data.',
      ]} />

      {/* Putting model */}
      <H2Heading>Putting Model</H2Heading>
      <ArticleParagraph lines={[
        'Putting make rates are calibrated to approximate published amateur statistics. The chart below compares our model\'s make probabilities at each distance against PGA Tour data (for reference) and typical ~15 handicap amateur figures.',
      ]} />

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="space-y-3">
          {PUTTING_REFERENCE_DATA.map(row => (
            <div key={row.distanceFt}>
              <span className="text-xs font-medium text-gray-700 mb-1 block">{row.label}</span>
              <div className="space-y-1">
                {/* PGA Tour */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-20 text-right shrink-0">PGA Tour</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{ width: `${row.pga}%`, backgroundColor: '#3B82F6' }}
                    />
                  </div>
                  <span className="text-xs text-blue-600 w-10 text-right shrink-0 font-medium">{row.pga}%</span>
                </div>
                {/* 15 Handicap */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-20 text-right shrink-0">15 Handicap</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{ width: `${row.amateur}%`, backgroundColor: '#F59E0B' }}
                    />
                  </div>
                  <span className="text-xs text-amber-600 w-10 text-right shrink-0 font-medium">{row.amateur}%</span>
                </div>
                {/* Our Model */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-20 text-right shrink-0">Our Model</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{ width: `${row.model}%`, backgroundColor: '#22C55E' }}
                    />
                  </div>
                  <span className="text-xs text-green-600 w-10 text-right shrink-0 font-medium">{row.model}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-5 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
            PGA Tour
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
            15 Handicap
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#22C55E' }} />
            Our Model
          </span>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-8">
        <span className="font-medium">Sources: </span>
        {PUTTING_SOURCES.map((src, i) => (
          <span key={src.name}>
            <a
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {src.name}
            </a>
            {i < PUTTING_SOURCES.length - 1 ? ', ' : ''}
          </span>
        ))}
      </div>

      {/* Hole-in-one probability */}
      <H2Heading>Hole-In-One Probability</H2Heading>
      <ArticleParagraph lines={[
        'A full shot holes out if it lands within 0.5m of the pin on the green. Because landing coordinates are rounded to a 1m grid, this means the ball must land on the exact pin cell — giving a hole-in-one probability of roughly 1 in 900 per par 3 with default player stats. This is more generous than real-world amateur rates (approximately 1 in 12,500 per par 3), but the difference is expected: our flat 2D model does not account for wind, elevation changes, green contour, or pin position difficulty, all of which make real hole-in-ones harder.',
        'Published statistics vary by skill level. Low handicappers see roughly 1 in 5,000 odds, while PGA Tour professionals sit at around 1 in 3,000. Our model\'s rate falls in a plausible range for a simplified simulation — hole-in-ones will appear occasionally in large batches (1,000+ simulations) but will be absent from most runs, which matches the spirit of how rare they are in practice.',
      ]} />

      <div className="text-xs text-gray-500 mb-8">
        <span className="font-medium">Sources: </span>
        <a href="https://www.hagginoaks.com/blog/what-are-your-chances-at-a-hole-in-one/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Haggin Oaks</a>
        {', '}
        <a href="https://www.holeinoneinsurance.com/hole-in-one-odds.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">US Hole In One</a>
        {', '}
        <a href="https://golf.com/instruction/hole-in-one-chances-handicap-data-liam-mucklow/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Golf.com</a>
      </div>
    </PageBody>
  );
};

export default HoleVisualisation;
